// =============================================================================
// VandieZ admin-web -- sidebar count badges
// =============================================================================
// Hook that polls five light endpoints every 60 s so the sidebar can
// surface "things waiting for you":
//
//   * KYC queue depth        GET /admin/kyc/queue?limit=1   .total
//   * Vehicle queue depth    GET /admin/vehicles/queue?limit=1   .total
//   * Open dispute count     GET /admin/disputes?status=open&limit=1   .total
//   * Open ticket count      GET /admin/tickets?status=open&limit=1    .total
//   * Unread inbox count     GET /users/me/notifications?only_unread=true
//                             .unread_count
//
// 60 s is plenty for operator-relevance; bumping faster would hammer
// the admin endpoints without buying useful freshness.  Failures are
// silent — the badge just falls back to `null` so the operator sees
// a clean nav rather than an error toast.
// =============================================================================

import { useCallback, useEffect, useState } from "react";

import {
  atRiskAdminApi,
  disputesAdminApi,
  inboxApi,
  kycAdminApi,
  supportAdminApi,
  vehicleAdminApi,
} from "@/api/client";

const POLL_MS = 60_000;

export interface NavCounts {
  kyc: number | null;
  vehicles: number | null;
  disputes: number | null;
  tickets: number | null;
  inbox: number | null;
  atRisk: number | null;
}

const EMPTY: NavCounts = {
  kyc: null,
  vehicles: null,
  disputes: null,
  tickets: null,
  inbox: null,
  atRisk: null,
};

export function useNavCounts(accessToken: string | null): NavCounts {
  const [counts, setCounts] = useState<NavCounts>(EMPTY);

  const fetchAll = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (!accessToken) {return;}
      const safe = async <T,>(
        p: Promise<T>,
      ): Promise<T | null> => {
        try {
          return await p;
        } catch {
          return null;
        }
      };

      const [kyc, vehicles, disputes, tickets, inbox, atRisk] = await Promise.all([
        safe(kycAdminApi.queue(accessToken, { limit: 1, offset: 0 }, signal)),
        safe(
          vehicleAdminApi.queue(
            accessToken,
            { limit: 1, offset: 0 },
            signal,
          ),
        ),
        safe(
          disputesAdminApi.queue(
            accessToken,
            { status: "open", limit: 1, offset: 0 },
            signal,
          ),
        ),
        safe(
          supportAdminApi.queue(
            accessToken,
            { status: "open", limit: 1, offset: 0 },
            signal,
          ),
        ),
        safe(
          inboxApi.list(
            accessToken,
            { only_unread: true, limit: 1, offset: 0 },
            signal,
          ),
        ),
        safe(atRiskAdminApi.list(accessToken, { limit: 500 }, signal)),
      ]);
      if (signal?.aborted === true) {return;}
      setCounts({
        kyc: kyc !== null ? kyc.total : null,
        vehicles: vehicles !== null ? vehicles.total : null,
        disputes: disputes !== null ? disputes.total : null,
        tickets: tickets !== null ? tickets.total : null,
        inbox: inbox !== null ? inbox.unread_count : null,
        atRisk: atRisk !== null ? atRisk.rows.length : null,
      });
    },
    [accessToken],
  );

  useEffect(() => {
    if (!accessToken) {
      setCounts(EMPTY);
      return undefined;
    }
    const controller = new AbortController();
    void fetchAll(controller.signal);
    const interval = setInterval(() => { void fetchAll(); }, POLL_MS);
    return () => {
      controller.abort();
      clearInterval(interval);
    };
  }, [accessToken, fetchAll]);

  return counts;
}
